import { ActionIcon, Badge, Group, Paper, Stack, Text, UnstyledButton } from '@mantine/core';
import { useState } from 'react';
import { IconPencil } from '@tabler/icons-react';
import { format, parseISO } from 'date-fns';
import { primaryColor } from '../../constants/theme';
import { MultiDatePickerModal } from './MultiDatePickerModal';

const groupByMonth = (dates: string[]): Record<string, string[]> => {
  return dates.reduce<Record<string, string[]>>((acc, iso) => {
    const key = format(parseISO(iso), 'MMM yyyy');
    if (!acc[key]) acc[key] = [];
    acc[key].push(iso);
    return acc;
  }, {});
};

interface SpecificDatesProps {
  selectedDates: string[];
  onDatesChange: (dates: string[]) => void;
}

export const SpecificDates = ({ selectedDates, onDatesChange }: SpecificDatesProps) => {
  const [showPicker, setShowPicker] = useState(false);

  const grouped = groupByMonth(selectedDates);
  const monthKeys = Object.keys(grouped);

  return (
    <Stack gap="sm">
      <Paper withBorder shadow="none" radius="sm" p="sm" style={{ cursor: 'pointer' }} onClick={() => setShowPicker(true)}>
        <Group justify="space-between" align="center">
          <Group gap={6}>
            <Stack gap={2}>
              <Text fw={600}>Selected Dates</Text>
              {selectedDates.length === 0 && (
                <Text size="xs" c="dimmed" ta="left">No dates selected</Text>
              )}
            </Stack>
            {selectedDates.length > 0 && (
              <Badge color={primaryColor} variant="filled" radius="sm" size="sm">
                {selectedDates.length}
              </Badge>
            )}
          </Group>
          <ActionIcon variant="subtle" color="gray" aria-label="Edit selected dates" component="div">
            <IconPencil size={16} />
          </ActionIcon>
        </Group>

        {monthKeys.length > 0 && (
          <Stack gap="xs" mt="sm">
            {monthKeys.map((monthKey) => (
              <Stack key={monthKey} gap={6}>
                <Text size="xs" c={primaryColor} fw={600} ta="left">
                  {monthKey}
                </Text>
                <Group gap={6} wrap="wrap">
                  {grouped[monthKey].map((iso) => (
                    <UnstyledButton
                      key={iso}
                      style={(theme) => ({
                        border: `1px solid ${theme.colors[primaryColor]?.[6] ?? primaryColor}`,
                        borderRadius: theme.radius.sm,
                        width: 36,
                        height: 36,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'default',
                      })}
                    >
                      <Text size="xs" fw={600} c={primaryColor}>
                        {format(parseISO(iso), 'd')}
                      </Text>
                    </UnstyledButton>
                  ))}
                </Group>
              </Stack>
            ))}
          </Stack>
        )}
      </Paper>

      <MultiDatePickerModal
        opened={showPicker}
        label="Selected Dates"
        initialDates={selectedDates}
        onClose={() => setShowPicker(false)}
        onConfirm={(dates) => {
          onDatesChange(dates);
          setShowPicker(false);
        }}
      />
    </Stack>
  );
};
